import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Crown, Radio, Copy, Trophy, Share2, Skull, Eye, LogIn } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import GameCanvas from "@/components/GameCanvas";
import CoopResults from "@/components/CoopResults";
import AchievementCard from "@/components/AchievementCard";
import { CoopChat } from "@/components/CoopChat";
import { Spinner } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function CoopPlay() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const canvasRef = useRef(null);
  const lastPostRef = useRef(0);
  const lastHttpRef = useRef(0);
  // Experimental realtime transport — opt-in via REACT_APP_MP_WS=1. Polling stays the default
  // and is used automatically whenever the socket isn't connected, so this can never break co-op.
  const MP_WS = process.env.REACT_APP_MP_WS === "1";
  const wsRef = useRef(null);
  const wsReadyRef = useRef(false);
  const wsPlayersRef = useRef({});

  const [room, setRoom] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [othersCount, setOthersCount] = useState(0);
  const prevScores = useRef({});
  const seenResults = useRef(new Set());
  const seenWinner = useRef(false);
  const prevRaceStart = useRef(null);
  const feedLen = useRef(0);
  const [countdown, setCountdown] = useState(null);
  const [finished, setFinished] = useState(false);
  const [raceKey, setRaceKey] = useState(0);
  const [gridPos, setGridPos] = useState(0);
  const [floatEmotes, setFloatEmotes] = useState([]);
  const seenChat = useRef(new Set());
  const rankedRef = useRef(null);
  const seenGuildXp = useRef(false);
  const [joining, setJoining] = useState(false);
  const inviteLink = `${window.location.origin}/app/coop/${roomId}`;
  const isSpectator = room?.me?.role === "spectator";
  const spectatorCount = (room?.spectators || []).length;
  const isKart = !!config && (config.template === "kart3d" || config.template === "racing3d");
  const isFps = !!config && config.template === "fps3d";
  const isBattle = !!config && config.template === "battle3d";
  const isSoccer = !!config && config.template === "soccer3d";
  const isCombat = isFps || isBattle;             // frag-based deathmatch (FPS + Battle Royale)
  const pvpMatch = isCombat && !!room?.match;
  const sportMatch = isSoccer && !!room?.match;   // soccer team goal race
  const anyMatch = pvpMatch || sportMatch;
  const myTeam = room?.me?.team || null;
  const gameConfig = useMemo(() => {
    if (!config) return null;
    const base = { ...config, player: { ...(config.player || {}), gridPos, team: myTeam } };
    if (isCombat && room?.match) { base.match = room.match; base.pvp = true; }
    if (isSoccer && room?.match) { base.match = room.match; base.mp = true; base.isHost = !!room?.is_host; }
    // Deep co-op sync — shared boss HP + downed/revive + squad HUD (3D adventure co-op only, not PvP/sport matches).
    if (config.dimension === "3d" && !isCombat && !isSoccer) {
      base.coop = true; base.coopHost = !!room?.is_host; base.coopId = user?.id; base.coopName = user?.name || "Player";
    }
    return base;
  }, [config, gridPos, isCombat, isSoccer, myTeam, room?.match, room?.is_host, user?.id, user?.name]);

  const spectatorConfig = useMemo(() => (gameConfig ? { ...gameConfig, spectator: true, coop: false } : null), [gameConfig]);

  // Load room (auto-join via invite link if not yet a member) + the game's config
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let { data: r } = await api.get(`/rooms/${roomId}`);
        if (!r.me && r.code) {
          try { await api.post("/rooms/join", { code: r.code }); r = (await api.get(`/rooms/${roomId}`)).data; } catch { /* room may be full/private */ }
        }
        if (cancelled) return;
        setRoom(r);
        (r.chat || []).forEach((c) => seenChat.current.add(c.id));
        const { data: g } = await api.get(`/arcade/${r.game_id}`);
        if (cancelled) return;
        setConfig(g.config);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Could not load this co-op room.");
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Send my live position to the room (throttled)
  const onPos = useCallback((d) => {
    const now = Date.now();
    if (now - lastPostRef.current < 150) return;
    lastPostRef.current = now;
    const payload = { x: d.x, y: d.y, z: d.z, h: d.h || 0, score: d.score || 0 };
    if (MP_WS && wsRef.current && wsReadyRef.current) {
      try { wsRef.current.send(JSON.stringify({ pos: payload })); } catch { /* fall through to http */ }
      // keep DB presence + score fresh at low frequency so the scoreboard still works
      if (now - lastHttpRef.current > 1000) { lastHttpRef.current = now; api.post(`/rooms/${roomId}/pos`, payload).catch(() => {}); }
      return;
    }
    lastHttpRef.current = now;
    api.post(`/rooms/${roomId}/pos`, payload).catch(() => {});
  }, [roomId, MP_WS]);

  // Realtime WebSocket lane (opt-in). Falls back to polling automatically if unavailable.
  useEffect(() => {
    if (!MP_WS || !config || isSoccer) return;
    const token = localStorage.getItem("gv_token");
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let ws, closed = false, retry;
    const connect = () => {
      try { ws = new WebSocket(`${base}/api/rooms/ws/${roomId}?token=${encodeURIComponent(token)}`); }
      catch { return; }
      wsRef.current = ws;
      ws.onopen = () => { wsReadyRef.current = true; };
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m.type === "pos" && m.user_id) {
            wsPlayersRef.current[m.user_id] = { user_id: m.user_id, name: m.name, avatar: m.avatar, team: m.team, pos: m.pos, _t: Date.now() };
            const now = Date.now();
            const players = Object.values(wsPlayersRef.current).filter((p) => now - p._t < 3000);
            setOthersCount(players.length);
            if (canvasRef.current?.ghosts) canvasRef.current.ghosts(players);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { wsReadyRef.current = false; wsRef.current = null; if (!closed) retry = setTimeout(connect, 1500); };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { ws && ws.close(); } catch { /* noop */ } wsRef.current = null; wsReadyRef.current = false; };
  }, [MP_WS, config, roomId, isSoccer]);

  // Poll other players' positions → render ghosts
  useEffect(() => {
    if (!config) return;
    const tick = async () => {
      if (MP_WS && wsReadyRef.current) return; // realtime socket is handling ghost positions
      try {
        const { data } = await api.get(`/rooms/${roomId}/pos`);
        const players = data.players || [];
        setOthersCount(players.length);
        if (!isSoccer && canvasRef.current?.ghosts) canvasRef.current.ghosts(players);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 350);
    return () => clearInterval(t);
  }, [config, roomId, isSoccer]);

  // Deep co-op sync — shared boss HP, downed/revive, squad status (3D adventure co-op only).
  const coopSeqRef = useRef(0);
  const [squadStats, setSquadStats] = useState({});
  const onCoopOut = useCallback((event) => {
    if (!event || !event.e) return;
    api.post(`/rooms/${roomId}/coop`, { event }).catch(() => {});
  }, [roomId]);
  const onCoopProfile = useCallback((d) => {
    if (d.type === "coop-squad-boss") {
      if (room?.is_host && (d.count || 0) > 0) api.post(`/rooms/${roomId}/coop/boss-felled`, { count: d.count }).catch(() => {});
      return;
    }
    const body = { medic: !!d.medic, rescues: d.rescues || 0, kills: d.kills || 0, boss_kill: !!d.boss_kill, best_roll: d.best_roll || 0 };
    api.post(`/rooms/coop/profile-stats`, body).then(({ data }) => {
      if (data?.coop_stats) patchUser({ coop_stats: data.coop_stats });
      (data?.newly_unlocked || []).forEach((a, i) => setTimeout(
        () => toast.success(`${a.icon} Achievement unlocked — ${a.name}!`, {
          description: "Saved to your Co-op record forever.",
          duration: 8000,
          action: { label: "Brag", onClick: () => setShareAch(a) },
        }),
        i * 750));
    }).catch(() => {});
  }, [patchUser, room?.is_host, roomId]);
  // MVP Replay Clip — the runner auto-records the boss's final blow; we hold the blob for the recap.
  const [mvpClip, setMvpClip] = useState(null);
  const [shareAch, setShareAch] = useState(null);
  const [squadClip, setSquadClip] = useState(null);
  const onCoopClip = useCallback((d) => {
    if (!d || !d.blob) return;
    try {
      const url = URL.createObjectURL(d.blob);
      setMvpClip((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return { url, mime: d.mime || "video/webm", blob: d.blob }; });
    } catch { /* ignore */ }
  }, []);
  // MVP posted their clip to Community Clips → broadcast the id so the whole squad can react in their recap.
  const onClipPosted = useCallback((clipId) => {
    if (!clipId) return;
    setSquadClip({ id: clipId, name: user?.name || "MVP", by: user?.id });
    api.post(`/rooms/${roomId}/coop`, { event: { e: "clip", clip: clipId } }).catch(() => {});
  }, [roomId, user?.id, user?.name]);
  useEffect(() => {
    if (!config || config.dimension !== "3d" || isCombat || isSoccer) return;
    coopSeqRef.current = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}/coop`, { params: { since: coopSeqRef.current } });
        if (typeof data.seq === "number") coopSeqRef.current = Math.max(coopSeqRef.current, data.seq);
        (data.events || []).forEach((ev) => {
          if (ev.e === "stats" && ev.by) setSquadStats((prev) => ({ ...prev, [ev.by]: { name: ev.name, kills: ev.kills || 0, revives: ev.revives || 0, roll: ev.roll || 0, bossDmg: ev.bossDmg || 0, score: ev.score || 0 } }));
          else if (ev.e === "clip" && ev.clip) setSquadClip({ id: ev.clip, name: ev.name || "MVP", by: ev.by });
        });
        if (canvasRef.current?.coopIn) canvasRef.current.coopIn({ events: data.events || [], boss: data.boss || {}, mates: data.mates || {}, mobs: data.mobs || {} });
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 350);
    return () => clearInterval(t);
  }, [config, roomId, isCombat, isSoccer]);

  // Poll room members for the live scoreboard + celebrate scores / kills / finishes / the winner
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/rooms/${roomId}`);
        setRoom(data);
        // Floating emotes — pop any new emote chat from others
        for (const c of data.chat || []) {
          if (seenChat.current.has(c.id)) continue;
          seenChat.current.add(c.id);
          if (c.kind === "emote" && c.user_id !== user?.id) {
            const fe = { key: c.id, emoji: c.body, name: c.name, left: 12 + Math.random() * 70 };
            setFloatEmotes((arr) => [...arr, fe]);
            setTimeout(() => setFloatEmotes((arr) => arr.filter((x) => x.key !== fe.key)), 2600);
          }
        }
        if (isCombat && data.match) {
          const feed = data.kill_feed || [];
          for (let i = feedLen.current; i < feed.length; i++) {
            const f = feed[i];
            if (f.victim_id === user?.id) { toast(`💀 ${f.killer_name} fragged you!`); canvasRef.current?.respawn?.(); }
            else if (f.killer_id === user?.id && f.victim_id) toast.success(`🎯 You fragged ${f.victim_name || "a rival"}!`);
          }
          feedLen.current = feed.length;
        } else {
          for (const m of data.members || []) {
            if (m.user_id === user?.id) continue;
            const prev = prevScores.current[m.user_id];
            if (prev != null && (m.score || 0) > prev) toast(`🎉 ${m.name} scored ${m.score}!`);
            prevScores.current[m.user_id] = m.score || 0;
          }
          for (const res of data.results || []) {
            if (!seenResults.current.has(res.user_id)) {
              seenResults.current.add(res.user_id);
              if (res.user_id !== user?.id) toast(`🏁 ${res.name} finished ${res.place === 1 ? "1st" : "#" + res.place}!`);
            }
          }
        }
        if (data.winner && !seenWinner.current) {
          seenWinner.current = true;
          const w = data.winner;
          const youWon = (w.user_id && w.user_id === user?.id) || (w.team && w.team === (data.me?.team));
          toast.success(youWon ? (anyMatch ? "🏆 You won the match!" : "🏆 You won the race!") : `🏆 ${w.name} wins!`);
          if (anyMatch) canvasRef.current?.setPaused?.(true);
        }
        if (data.status === "ended" || (data.results || []).some((r) => r.user_id === user?.id)) setFinished(true);
        // Guild XP payoff — celebrate when a win boosts the player's guild(s)
        const myRes = (data.results || []).find((r) => r.user_id === user?.id);
        if (myRes?.guild_xp?.length && !seenGuildXp.current) {
          seenGuildXp.current = true;
          myRes.guild_xp.forEach((gx) => toast.success(`⚔️ +${gx.amount} XP → ${gx.name}!`, { description: "Your win boosted your guild's Guild Wars score." }));
        }
        // Ranked ladder — record competitive results once per match (FPS deathmatch + kart races, needs a real opponent)
        const competitive = !!data.match || ["kart3d", "racing3d"].includes(data.game_template);
        const hasOpponent = (data.members || []).length >= 2;
        const mine = (data.results || []).find((r) => r.user_id === user?.id);
        if (competitive && hasOpponent && mine && rankedRef.current !== data.race_start_at) {
          rankedRef.current = data.race_start_at;
          api.post("/ranked/record", { place: mine.place || 1, total: (data.results || []).length || (data.members || []).length || 1, won: !!mine.won, match_id: `${roomId}:${data.race_start_at}`, game_id: data.game_id })
            .then(({ data: rd }) => { if (rd.points_awarded && !rd.duplicate) toast.success(`🏅 +${rd.points_awarded} ranked points!`); })
            .catch(() => {});
        }
      } catch { /* ignore */ }
    }, isCombat ? 1500 : (sportMatch ? 2000 : 3000));
    return () => clearInterval(t);
  }, [roomId, user, isCombat, sportMatch, anyMatch]);

  // Detect a new race/match (first start or host rematch) → reset local race state + remount the game
  useEffect(() => {
    if (!room?.race_start_at) return;
    if (prevRaceStart.current && prevRaceStart.current !== room.race_start_at) {
      setFinished(false);
      setRaceKey((k) => k + 1);
      seenWinner.current = false;
      seenResults.current.clear();
      prevScores.current = {};
      feedLen.current = 0;
      setMvpClip((prev) => { if (prev?.url) URL.revokeObjectURL(prev.url); return null; });
      setSquadClip(null);
    }
    prevRaceStart.current = room.race_start_at;
  }, [room?.race_start_at]);

  // Synchronized 3·2·1·GO countdown for racing & FPS deathmatch rooms (driven by the shared race_start_at)
  const isMatchRace = isKart || pvpMatch || sportMatch;
  useEffect(() => {
    if (!isMatchRace || !room?.race_start_at) { setCountdown(null); return; }
    const startMs = Date.parse(room.race_start_at);
    if (!startMs || startMs <= Date.now()) { setCountdown(null); return; } // late joiner — race already live
    let iv, goTo;
    const tick = () => {
      const rem = startMs - Date.now();
      if (rem <= 0) {
        setCountdown("GO!");
        canvasRef.current?.setPaused?.(false);
        goTo = setTimeout(() => setCountdown(null), 900);
        clearInterval(iv);
        return;
      }
      setCountdown(Math.ceil(rem / 1000));
      canvasRef.current?.setPaused?.(true);
    };
    tick();
    iv = setInterval(tick, 250);
    return () => { clearInterval(iv); clearTimeout(goTo); };
  }, [isMatchRace, room?.race_start_at]);

  const onGameOver = useCallback(async (d) => {
    setFinished(true);
    try { await api.post(`/rooms/${roomId}/score`, { score: d.score || 0 }); } catch { /* ignore */ }
    try { await api.post(`/rooms/${roomId}/finish`, { score: d.score || 0, won: !!d.won }); } catch { /* ignore */ }
    if (room?.game_id) {
      try { await api.post(`/arcade/${room.game_id}/score`, { score: d.score || 0 }); } catch { /* ignore */ }
      if (d.metrics && d.metrics.metric) {
        try { await api.post(`/arcade/${room.game_id}/stat`, { metric: d.metrics.metric, value: d.metrics.value, extra: d.metrics.extra ?? undefined }); } catch { /* ignore */ }
      }
      if (d.metrics && d.metrics.place) {
        try { await api.post(`/racing/record`, { game_id: room.game_id, place: d.metrics.place, total: d.metrics.total || 4 }); } catch { /* ignore */ }
      }
    }
  }, [roomId, room]);

  const rematch = useCallback(async () => {
    const mine = (room?.results || []).find((r) => r.user_id === user?.id);
    setGridPos(mine ? Math.max(0, mine.place - 1) : 0); // previous placement → starting grid slot
    const opts = room?.match ? (room.match.sport
      ? { goal_target: room.match.frag_limit, time_limit: room.match.time_limit }
      : { mode: room.match.kind, frag_limit: room.match.frag_limit, time_limit: room.match.time_limit }) : {};
    try { await api.post(`/rooms/${roomId}/start`, opts); setFinished(false); toast.success("Rematch! Get ready…"); }
    catch (e) { toast.error("Only the host can start a rematch."); }
  }, [roomId, room, user]);

  const onScore = useCallback((s) => {
    const now = Date.now();
    if (now - (lastPostRef.current || 0) < 1500) return; // piggyback throttle for score sync
    api.post(`/rooms/${roomId}/score`, { score: s || 0 }).catch(() => {});
  }, [roomId]);

  const onFrag = useCallback((d) => {
    api.post(`/rooms/${roomId}/frag`, { victim_id: d?.victim_id || null }).catch(() => {});
  }, [roomId]);

  const sendChat = useCallback((body, kind = "text") => {
    api.post(`/rooms/${roomId}/chat`, { body, kind })
      .then(({ data }) => setRoom(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send"));
  }, [roomId]);

  const joinAsPlayer = useCallback(async () => {
    setJoining(true);
    try { const { data } = await api.post(`/rooms/${roomId}/join`); setRoom(data); toast.success("You're in — playing now!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't join — the room may be full."); }
    finally { setJoining(false); }
  }, [roomId]);

  const copyCode = () => { navigator.clipboard?.writeText(room?.code || ""); toast.success("Room code copied!"); };
  const copyInvite = () => { navigator.clipboard?.writeText(inviteLink); toast.success("Invite link copied — send it to a friend to play together!"); };

  if (error) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center">
        <p className="text-lg font-bold mb-2">Co-op room unavailable</p>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{error}</p>
        <Button onClick={() => navigate("/app/multiplayer")} className="rounded-full" data-testid="coop-back-error"><ArrowLeft className="w-4 h-4" /> Back to Multiplayer</Button>
      </div>
    );
  }
  if (!room || !config) return <Spinner />;

  const members = [...(room.members || [])].sort((a, b) => (b.score || 0) - (a.score || 0));
  const is3d = config.dimension === "3d";
  const isTeam = anyMatch && room.match?.kind === "team";
  const teamColor = (t) => (t === "red" ? "text-rose-500" : t === "blue" ? "text-sky-500" : "");
  const teamTotals = isTeam
    ? (room.members || []).reduce((acc, m) => { if (m.team === "red") acc.red += (m.score || 0); if (m.team === "blue") acc.blue += (m.score || 0); return acc; }, { red: 0, blue: 0 })
    : null;
  const killFeed = [...(room.kill_feed || [])].slice(-6).reverse();

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="coop-play-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/multiplayer")} data-testid="coop-back"><ArrowLeft className="w-4 h-4" /> Multiplayer</Button>
        <div className="flex items-center gap-2">
          <Badge className="gap-1 bg-emerald-500/15 text-emerald-500 border-0"><Radio className="w-3 h-3" /> Co-op live</Badge>
          {isSpectator && <Badge className="gap-1 bg-violet-500/15 text-violet-500 border-0" data-testid="coop-spectating-badge"><Eye className="w-3 h-3" /> Spectating</Badge>}
          {spectatorCount > 0 && <Badge variant="secondary" className="gap-1" data-testid="coop-spectator-count"><Eye className="w-3 h-3" /> {spectatorCount} watching</Badge>}
          <button onClick={copyCode} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1 text-sm font-mono tracking-widest hover:bg-[hsl(var(--accent))]" data-testid="coop-code">
            {room.code} <Copy className="w-3.5 h-3.5" />
          </button>
          <Button size="sm" className="rounded-full gap-1.5" onClick={copyInvite} data-testid="coop-invite-link"><Share2 className="w-4 h-4" /> Invite friends</Button>
        </div>
      </div>

      <h1 className="font-display text-2xl lg:text-3xl font-extrabold flex items-center gap-2 mb-1">{room.game_cover} {room.game_title}</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
        {pvpMatch
          ? <>{isTeam ? "Team Deathmatch" : "Free-for-all Deathmatch"} · first to {room.match?.frag_limit} kills (or most when time runs out) wins.{myTeam ? <> You're on the <b className={teamColor(myTeam)}>{myTeam === "red" ? "Red" : "Blue"} team</b>.</> : ""}</>
          : sportMatch
          ? <>Team Soccer · first team to {room.match?.frag_limit} goals (or ahead when time runs out) wins.{myTeam ? <> You're on the <b className={teamColor(myTeam)}>{myTeam === "red" ? "Red" : "Blue"} team</b>.</> : ""}</>
          : <>Playing together with {othersCount} other {othersCount === 1 ? "player" : "players"} live.{!is3d && " (Live ghosts show in 3D games — here you'll share the scoreboard.)"}</>}
      </p>

      {room.winner && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 mb-4 flex items-center gap-2" data-testid="coop-winner-banner">
          <Trophy className="w-5 h-5 text-amber-400 shrink-0" />
          <span className="font-bold">{room.winner.avatar} {room.winner.name}{room.winner.user_id === user?.id ? " (you)" : ""} {isCombat ? "wins the match! 🔫" : sportMatch ? "wins the match! ⚽" : "won the race! 🏁"}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {isSpectator ? (
            <div data-testid="coop-spectator-view">
              {is3d ? (
                <div className="relative">
                  <GameCanvas key={raceKey} ref={canvasRef} config={spectatorConfig} compact />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-violet-500/90 text-white px-3 py-1 text-xs font-semibold" data-testid="coop-spectate-3d-badge"><Eye className="w-3.5 h-3.5" /> Spectating live</div>
                  {othersCount === 0 && (
                    <div className="absolute inset-0 grid place-items-center bg-black/40 rounded-xl pointer-events-none">
                      <p className="text-white/90 text-sm">Waiting for players to start moving…</p>
                    </div>
                  )}
                  {floatEmotes.length > 0 && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="coop-float-emotes">
                      {floatEmotes.map((fe) => (
                        <div key={fe.key} className="absolute bottom-4 text-3xl text-center" style={{ left: `${fe.left}%`, animation: "floatUp 2.6s ease-out forwards" }}>
                          {fe.emoji}<span className="block text-[10px] text-white/80 -mt-1">{fe.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 text-center">
                  <div className="w-14 h-14 rounded-2xl grid place-items-center bg-violet-500/15 text-violet-500 mx-auto mb-3"><Eye className="w-7 h-7" /></div>
                  <h3 className="font-display text-xl font-bold mb-1">You're spectating</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">
                    Follow the action live — the scoreboard{isFps ? ", kill feed" : ""} and quick chat all update in real time. Cheer the players on with emotes!
                  </p>
                </div>
              )}
              <div className="rounded-xl border border-[hsl(var(--border))] p-3 mt-3 text-left" data-testid="coop-field-players">
                <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Radio className="w-3.5 h-3.5 text-emerald-500" /> On the field ({othersCount})</div>
                {othersCount === 0 ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Waiting for players to start moving…</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {members.filter((m) => m.role !== "spectator").map((m) => (
                      <span key={m.user_id} className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-2.5 py-1 text-sm">
                        {m.online && <span className="w-2 h-2 rounded-full bg-emerald-500" />}{m.avatar} {m.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 text-center">
                <Button onClick={joinAsPlayer} disabled={joining} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="coop-join-as-player">
                  <LogIn className="w-4 h-4" /> {joining ? "Joining…" : "Jump in and play"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="relative">
              <GameCanvas key={raceKey} ref={canvasRef} config={gameConfig} onGameOver={onGameOver} onScore={isCombat ? undefined : onScore} onPos={isSoccer ? undefined : onPos} onFrag={isCombat ? onFrag : undefined} onCoopOut={(isCombat || isSoccer) ? undefined : onCoopOut} onCoopProfile={(isCombat || isSoccer) ? undefined : onCoopProfile} onCoopClip={(isCombat || isSoccer) ? undefined : onCoopClip} />
              {floatEmotes.length > 0 && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="coop-float-emotes">
                  {floatEmotes.map((fe) => (
                    <div key={fe.key} className="absolute bottom-4 text-3xl text-center" style={{ left: `${fe.left}%`, animation: "floatUp 2.6s ease-out forwards" }}>
                      {fe.emoji}<span className="block text-[10px] text-white/80 -mt-1">{fe.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {countdown != null && (
                <div className="absolute inset-0 grid place-items-center bg-black/45 rounded-xl pointer-events-none" data-testid="coop-countdown">
                  <div className="text-center">
                    <div className="text-7xl font-display font-extrabold text-white drop-shadow-lg animate-pulse">{countdown}</div>
                    <div className="text-sm text-white/80 mt-2">{pvpMatch ? "Get ready — fight!" : sportMatch ? "Get ready — kick off!" : "Get ready — race starting together!"}</div>
                  </div>
                </div>
              )}
            </div>
          )}
          {is3d && !isSpectator && <p className="text-xs text-center mt-2 text-[hsl(var(--muted-foreground))]">{sportMatch ? "⚽ Score goals on your own pitch — every goal you net adds to your team's total! Click the game to focus, then play!" : pvpMatch ? "🔫 Translucent avatars are rival players — aim at them to frag. Click the game to focus, then play!" : "👻 Translucent avatars are your friends moving in real time. Click the game to focus, then play!"}</p>}
        </div>

        <div className="space-y-4">
          {isTeam && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 flex items-center justify-around" data-testid="coop-team-totals">
              <div className="text-center"><div className="text-2xl">🔴</div><div className="font-display text-2xl font-extrabold text-rose-500 tabular-nums" data-testid="team-red-score">{teamTotals.red}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Red Team</div></div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">to {room.match?.frag_limit}</div>
              <div className="text-center"><div className="text-2xl">🔵</div><div className="font-display text-2xl font-extrabold text-sky-500 tabular-nums" data-testid="team-blue-score">{teamTotals.blue}</div><div className="text-[11px] text-[hsl(var(--muted-foreground))]">Blue Team</div></div>
            </div>
          )}
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2">{isCombat ? <Skull className="w-5 h-5 text-rose-500" /> : <Trophy className="w-5 h-5 text-amber-500" />} {isCombat ? "Live kills" : sportMatch ? "Team goals" : "Live scoreboard"}</h3>
            <div className="space-y-2" data-testid="coop-scoreboard">
              {members.map((m, i) => (
                <div key={m.user_id} className={`flex items-center justify-between text-sm ${m.user_id === user?.id ? "font-bold text-[hsl(var(--primary))]" : ""}`} data-testid={`coop-score-${i}`}>
                  <span className="flex items-center gap-2 truncate">
                    <span className="w-5 text-center">{["🥇", "🥈", "🥉"][i] || i + 1}</span>
                    {m.team && <span title={m.team}>{m.team === "red" ? "🔴" : "🔵"}</span>}
                    <span>{m.avatar}</span>
                    <span className={`truncate ${teamColor(m.team)}`}>{m.name}{m.user_id === user?.id && " (you)"}</span>
                    {m.role === "host" && <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                    {m.online && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Online" />}
                  </span>
                  <span className="tabular-nums">{m.score || 0}{isFps ? " 💀" : ""}</span>
                </div>
              ))}
            </div>
          </div>

          {pvpMatch && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="coop-kill-feed">
              <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Skull className="w-5 h-5 text-[hsl(var(--muted-foreground))]" /> Kill feed</h3>
              {killFeed.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">No frags yet — go get 'em!</p>
              ) : (
                <div className="space-y-1.5 text-sm">
                  {killFeed.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`kill-feed-${i}`}>
                      <span className="font-semibold">{f.killer_name}</span>
                      <Skull className="w-3.5 h-3.5 text-rose-500" />
                      <span className="text-[hsl(var(--muted-foreground))]">{f.victim_name || "a bot"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
            <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Users className="w-5 h-5" /> In this room</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Share the code <b className="font-mono tracking-widest text-[hsl(var(--foreground))]">{room.code}</b> so friends can jump in. Their characters appear live in your world.</p>
          </div>

          <CoopChat chat={room.chat || []} onSend={sendChat} meId={user?.id} />
        </div>
      </div>

      <CoopResults
        open={finished}
        onOpenChange={(v) => setFinished(v)}
        room={room}
        user={user}
        gameCover={room.game_cover}
        gameTitle={room.game_title}
        isHost={!!room.is_host}
        onRematch={rematch}
        onLeave={() => navigate("/app/multiplayer")}
        coopMode={is3d && !isCombat && !isSoccer}
        squadStats={squadStats}
        mvpClipUrl={mvpClip?.url}
        mvpClipMime={mvpClip?.mime}
        mvpClipBlob={mvpClip?.blob}
        squadClip={squadClip}
        onClipPosted={onClipPosted}
      />

      <AchievementCard achievement={shareAch} user={user} open={!!shareAch} onOpenChange={(v) => { if (!v) setShareAch(null); }} />
    </div>
  );
}
